import {ImmutableStore} from 'fluxible-immutable-utils';
import Actions from '../constants/Actions';
import {scaleLinear} from 'd3-scale';
import Immutable from 'immutable';
import { keyText } from '../intl/ActionMessages';
import { startOfDay } from '../utils/Date';
import { parseIntOr } from '../utils/UrlParamParsers';

const sortKeys = [
  {
    key: 'conversationScore',
    value: 'Efficiency Score'
  }, {
    key: 'guideCompletionPercentage',
    value: 'Guide Completion %'
  }, {
    key: 'guidesCompleted',
    value: 'Guides Completed'
  }
];

function defaultState() {
  return {
    data: [],
    aggregationLevel: 0,
    sortKey: 'conversationScore',
    actionType: 'percentage',
    sortKeys,
    actionTypes: [],
    ascending: false,
    guideType: 'both',
    loading: false,
    customerType: 'both'
  };
}

const percentageTest = /Percentage$/;

const domains = {
  conversationScore: [0, 100],
  accountScore: [0, 100]
};

function setFocused(store, leaderData = Immutable.Seq(), focusedKey) {
  const focusedObj = leaderData.findEntry(leader => {
    return leader.get('key') === focusedKey;
  });

  if (focusedObj) {
    store.focusedObj = focusedObj[1];
    store.focusedIndex = focusedObj[0] + 1;
  } else {
    delete store.focusedObj;
    delete store.focusedIndex;
  }
}

function sortData(data, sortKey, ascending) {
  const sorted = data.sortBy(e => {
    return (e.getIn(sortKey.split('.')) || 0) * -1;
  });

  return ascending ? sorted.reverse() : sorted;
}

class TopSourcesStore extends ImmutableStore {
  static storeName = 'TopSourcesStore';

  static handlers = {
    [Actions.ONLINE_LEADER_DATA_CHANGED]: 'handleOnlineLeaderDataChanged',
    [Actions.ONLINE_LEADER_QUERY_CHANGED]: 'handleOnlineLeaderQueryChanged'
  };

  constructor(dispatcher) {
    super(dispatcher);

    const now = startOfDay();
    const before = startOfDay().subtract(1, 'month');

    this.startDate = before.toDate();
    this.endDate = now.toDate();
    this.setState(defaultState());
  }

  handleOnlineLeaderQueryChanged(query) {
    const defaults = defaultState();
    const {
      sortKey = defaults.sortKey,
      sortDirection = defaults.sortDirection,
      focused,
      startDate = startOfDay().subtract(1, 'month'),
      endDate = startOfDay(),
      aggregationLevel = defaults.aggregationLevel,
      guideType = defaults.guideType,
      customerType = defaults.customerType,
      actionType = defaults.actionType
    } = query;

    if (startDate || endDate) {
      this.startDate = startDate ? new Date(startDate) : this.startDate;
      this.endDate = endDate ? new Date(endDate) : this.endDate;
      this.emitChange();
    }

    const ascending = sortDirection === 'ascending';
    const data = sortData(this.get('data'), sortKey, ascending);
    setFocused(this, data, focused);

    this.mergeState({
      ascending,
      sortKey,
      aggregationLevel: parseIntOr(aggregationLevel, defaults.aggregationLevel),
      guideType,
      data,
      focused,
      customerType,
      actionType
    });
  }

  handleOnlineLeaderDataChanged(payload) {
    const {leaderData, loading = false} = payload;

    console.log('Updating leaderboard store state', payload);

    const state = {
      loading
    };

    if (leaderData) {
      const sortKey = this.get('sortKey');
      const ascending = this.get('ascending');
      const immutableData = Immutable.fromJS(leaderData);
      let actionSortKeys = Immutable.Set();
      const actionKeys = [];
      const actionTypes = {};
      immutableData.forEach(d => {
        d.get('actions').forEach((actionValue, actionKey) => {
          if (!actionTypes[actionKey]) {
            actionTypes[actionKey] = {
              values: Immutable.Set()
            };
          }
          const actionType = actionTypes[actionKey];
          actionValue.forEach((value, key) => {
            actionType.values = actionType.values.add(key);
            const actionSortKey = `${actionKey}.${key}`;
            if (!actionSortKeys.contains(actionSortKey)) {
              actionSortKeys = actionSortKeys.add(actionSortKey);
              actionKeys.push({
                key: `actions.${actionSortKey}`,
                value: keyText[actionSortKey] || actionSortKey
              });
            }
          });
        });
      });
      const sortedActionKeys = Immutable.fromJS(actionKeys).sortBy(e => e.get('value')).toJS();
      const sortedData = sortData(immutableData, sortKey, ascending);
      setFocused(this, sortedData, this.get('focused'));
      state.data = sortedData;
      state.actionTypes = actionTypes;
      state.sortKeys = sortKeys.concat(sortedActionKeys);
    }

    this.mergeState(state);
  }

  getLeaderData() {
    const sortKey = this.get('sortKey');
    const sorted = this.get('data');
    const scale = scaleLinear();
    let domain = domains[sortKey];

    if (percentageTest.test(sortKey)) {
      domain = [0 ,1];
    }
    if (!domain) {
      const values = sorted.map(e => {
        return e.getIn(sortKey.split('.')) || 0;
      }).toJS();
      let min = 0, max = 1;
      if (values.length) {
        min = Math.min(...values);
        max = Math.max(...values);
      }

      domain = [min, max];
    }

    const leaderScale = scale.domain(domain).nice().range([0, 100]).clamp(true);

    return {
      leaderData: sorted.toJS(),
      leaderScale
    };
  }

  isAscendingSort() {
    return this.get('ascending');
  }

  getFocusedLeader() {
    return this.focusedObj;
  }

  getFocusedIndex() {
    return this.focusedIndex || 0;
  }

  getLeaderCount() {
    const data = this.get('data');

    return data ? data.size : 0;
  }

  getSortKey() {
    return this.get('sortKey');
  }

  getSortKeys() {
    return this.get('sortKeys').toJS();
  }

  getGuideType() {
    return this.get('guideType');
  }

  getSelectedDateRange() {
    return {
      start: this.startDate,
      end: this.endDate
    };
  }

  getActionType() {
    return this.get('actionType');
  }

  getCustomerType() {
    return this.get('customerType');
  }

  getAggregationLevel() {
    return this.get('aggregationLevel');
  }

  isLoading() {
    return this.get('loading');
  }

  rehydrate(state) {
    super.rehydrate(state._state);

    this.startDate = new Date(state.startDate);
    this.endDate = new Date(state.endDate);
    this.focusedObj = Immutable.fromJS(state.focusedObj);
    this.focusedIndex = state.focusedIndex;
  }

  dehydrate() {
    return {
      _state: this._state,
      startDate: this.startDate,
      endDate: this.endDate,
      focusedObj: this.focusedObj,
      focusedIndex: this.focusedIndex
    };
  }
}

export default TopSourcesStore;



// WEBPACK FOOTER //
// ./src/stores/TopSourcesStore.js